/**
 * Home della squadra-stagione (V06, il cruscotto della specifica): il punto
 * d'ingresso. In testa il saluto con la partita sul campo o il prossimo
 * impegno; sotto i numeri della stagione, l'agenda della settimana, gli
 * ultimi risultati, gli avvisi e i marcatori. Ogni blocco porta alla vista
 * di dettaglio.
 *
 * Tutto arriva da una richiesta sola, gia filtrato per ruolo: chi non legge
 * i voti non riceve ne l'avviso delle partite senza voti ne la media. Con una
 * partita in corso il minuto si aggiorna ricaricando ogni mezzo minuto.
 */

import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ActivityIcon,
  BusIcon,
  CalendarDaysIcon,
  CalendarX2Icon,
  ChevronRightIcon,
  CircleCheckIcon,
  ClipboardXIcon,
  ClockIcon,
  DumbbellIcon,
  GoalIcon,
  HouseIcon,
  MapPinIcon,
  StarIcon,
  TrendingUpIcon,
  TriangleAlertIcon,
  TrophyIcon,
  UsersIcon,
  type LucideIcon,
} from 'lucide-react';
import { puo } from '@calcio/shared';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { ElencoPassi } from '../comuni/ElencoPassi.js';
import { StatoCaricamento, Testata } from '../comuni/Testata.js';
import { Corpo } from '../comuni/Corpo.js';
import { formatoIstante } from '../comuni/date.js';
import { useDati } from '../comuni/dati.js';
import { useContesto } from '../navigazione/contesto.js';
import {
  formatoGiornoSettimana,
  formatoMeseBreve,
  formatoNumeroGiorno,
  formatoOra,
  formatoRiga,
} from '../allenamenti/allenamenti.js';
import { DialogBlocco } from '../allenamenti/BloccoAllenamenti.js';
import { DialogAllenamento } from '../allenamenti/FormAllenamento.js';
import { COLORE_ESITO, ETICHETTA_ESITO, LETTERA_ESITO, esitoDi } from '../partite/raggruppa.js';
import { IconaEvento } from '../partite/eventi.js';
import { punteggio } from '../partite/partita.js';
import { CardSezione } from '../test-misura/CardSezione.js';
import type {
  ChiavePasso,
  Cruscotto as DatiCruscotto,
  PartitaInBreve,
  Passi,
} from './cruscotto.js';
import { contaAvvisi, elencoPassi, quando, saluto } from './cruscotto.js';
import { BadgeStato, CartellinoSportivo } from '../giocatori/Stati.js';
import { StatoVuoto } from '../comuni/StatoVuoto.js';

const RICARICA_IN_CORSO_MS = 30_000;

const formatoOggi = formatoIstante({ weekday: 'long', day: 'numeric', month: 'long' });
const numero = (n: number) => n.toLocaleString('it-IT', { maximumFractionDigits: 1 });
const percentuale = (q: number) => `${Math.round(q * 100)}%`;

export function PaginaCruscotto() {
  const { ss } = useParams<{ ss: string }>();
  const { utente, squadre } = useContesto();
  const programma = Boolean(ss && puo(utente.assegnazioni, 'allenamento.gestisci', ss));
  const { dati, errore, ricarica } = useDati<DatiCruscotto>(`/squadre-stagione/${ss}/cruscotto`);
  const inGioco = Boolean(dati?.inCorso?.cronometro);
  const adesso = new Date();
  const squadra = squadre?.find((s) => s.id === ss);

  useEffect(() => {
    if (!inGioco) return;
    const t = setInterval(() => void ricarica(), RICARICA_IN_CORSO_MS);
    return () => clearInterval(t);
  }, [inGioco, ricarica]);

  // Il titolo e la squadra, con la societa accanto e la data a destra, nei
  // caratteri delle altre testate: la voce del menu dice gia che e la Home.
  // Societa e data stanno in basso, piu o meno sulla linea di base del titolo.
  const testata = (
    <Testata
      titolo={squadra ? `${squadra.squadra} ${squadra.stagione}` : 'Home'}
      accanto={
        utente.societa && (
          <span className="self-end pb-0.5 text-[13px] text-muted-foreground">
            {utente.societa}
          </span>
        )
      }
      azione={
        <span className="self-end pb-0.5 text-[13px] text-muted-foreground first-letter:uppercase">
          {formatoOggi.format(adesso)}
        </span>
      }
    />
  );

  if (!dati) {
    return (
      <div>
        {testata}
        <Corpo>
          <StatoCaricamento errore={errore} />
        </Corpo>
      </div>
    );
  }

  const base = `/sq/${ss}`;

  if (dati.senzaRosa) {
    return (
      <div>
        {testata}
        <Corpo>
          {dati.passi ? (
            <PassiGuidati base={base} passi={dati.passi} />
          ) : (
            <InPreparazione ss={ss!} programma={programma} />
          )}
        </Corpo>
      </div>
    );
  }

  const { inCorso, prossimo, agenda, stagione, allenamenti, ultimiRisultati, avvisi, rosa } = dati;
  const disponibili = rosa.tesserati - avvisi.nonDisponibili.length;
  // Il prossimo impegno sta gia nella banda in testa: l'agenda mostra il resto.
  const inAgenda = agenda.filter((i) => i.id !== prossimo?.id);

  return (
    <div>
      {testata}
      <Corpo className="flex flex-col gap-4 @4xl:gap-6">
        <section className="flex flex-col gap-3 rounded-xl bg-primary p-3 text-primary-foreground @3xl:flex-row @3xl:items-center @3xl:justify-between @3xl:gap-6 @3xl:p-4 @3xl:pl-6">
          <h2 className="px-1 text-2xl font-bold @3xl:px-0 @3xl:text-3xl">
            {saluto(adesso)}, {utente.nome}
          </h2>
          {inCorso ? (
            <SulCampo base={base} partita={inCorso} />
          ) : (
            <Prossimo base={base} prossimo={prossimo} adesso={adesso} />
          )}
        </section>

        <div className="grid grid-cols-2 gap-3 @4xl:grid-cols-4 @4xl:gap-4">
          <Numero
            a={`${base}/partite`}
            Icona={TrophyIcon}
            etichetta="Stagione"
            valore={
              stagione.giocate === 0 ? (
                '—'
              ) : (
                <>
                  {stagione.vinte}
                  <span className="text-muted-foreground">·</span>
                  {stagione.pareggiate}
                  <span className="text-muted-foreground">·</span>
                  {stagione.perse}
                </>
              )
            }
            nota={
              stagione.giocate === 0
                ? 'Nessuna partita conclusa'
                : `V · N · P su ${stagione.giocate} ${stagione.giocate === 1 ? 'partita' : 'partite'}`
            }
          >
            {stagione.forma.length > 0 && (
              <span className="flex gap-1" aria-label="Forma, dalla più recente">
                {stagione.forma.map((e, i) => (
                  <Badge
                    key={i}
                    className={cn('w-6 px-0', COLORE_ESITO[e])}
                    title={ETICHETTA_ESITO[e]}
                  >
                    {LETTERA_ESITO[e]}
                  </Badge>
                ))}
              </span>
            )}
          </Numero>

          <Numero
            a={`${base}/statistiche`}
            Icona={GoalIcon}
            etichetta="Gol"
            valore={
              stagione.giocate === 0 ? (
                '—'
              ) : (
                <>
                  {stagione.golFatti}
                  <span className="text-muted-foreground">–</span>
                  {stagione.golSubiti}
                </>
              )
            }
            nota={
              stagione.giocate === 0
                ? 'Fatti e subiti'
                : `${numero(stagione.golFatti / stagione.giocate)} fatti e ${numero(stagione.golSubiti / stagione.giocate)} subiti a partita`
            }
          >
            {stagione.giocate > 0 && <Differenza valore={stagione.golFatti - stagione.golSubiti} />}
          </Numero>

          <Numero
            a={`${base}/rosa`}
            Icona={UsersIcon}
            etichetta="Disponibili"
            valore={
              <span>
                {disponibili}
                <span className="text-lg text-muted-foreground">/{rosa.tesserati}</span>
              </span>
            }
            nota={
              rosa.mediaVoto === undefined
                ? 'Tesserati in rosa'
                : `Voto medio ${rosa.mediaVoto === null ? '—' : numero(rosa.mediaVoto)}, ultime 5`
            }
          >
            <Progress
              value={rosa.tesserati > 0 ? (disponibili / rosa.tesserati) * 100 : 0}
              aria-label="Disponibili sui tesserati"
            />
          </Numero>

          <Numero
            a={`${base}/allenamenti`}
            Icona={DumbbellIcon}
            etichetta="Presenza"
            valore={allenamenti.presenza === null ? '—' : percentuale(allenamenti.presenza)}
            nota={
              allenamenti.svolti === 1
                ? '1 allenamento svolto'
                : `${allenamenti.svolti} allenamenti svolti`
            }
          >
            <Progress
              value={(allenamenti.presenza ?? 0) * 100}
              aria-label="Presenza agli allenamenti"
            />
          </Numero>
        </div>

        {/* Una colonna esplicita: senza, le righe troncate lunghe allargano la griglia. */}
        <div className="grid grid-cols-1 items-start gap-4 @4xl:grid-cols-3 @4xl:gap-6">
          <div className="flex flex-col gap-4 @4xl:col-span-2 @4xl:gap-6">
            <CardSezione
              titolo="Settimana"
              Icona={CalendarDaysIcon}
              conteggio={inAgenda.length > 0 ? inAgenda.length : undefined}
            >
              {inAgenda.length === 0 && (
                <StatoVuoto
                  titolo={
                    prossimo
                      ? 'Nient’altro in programma nei prossimi sette giorni'
                      : 'Nessun impegno nei prossimi sette giorni'
                  }
                />
              )}
              {inAgenda.map((i) =>
                i.tipo === 'partita' ? (
                  <Riga key={i.id} a={`${base}/partite/${i.id}`} data={i.dataOra}>
                    <span className="truncate font-semibold">{i.avversario}</span>
                    <Dettagli>
                      <Dettaglio Icona={ClockIcon}>
                        {formatoOra.format(new Date(i.dataOra))}
                      </Dettaglio>
                      <Dettaglio Icona={i.inCasa ? HouseIcon : BusIcon}>
                        {i.inCasa ? 'Casa' : 'Trasferta'}
                      </Dettaglio>
                      <Dettaglio Icona={TrophyIcon}>{i.competizione}</Dettaglio>
                    </Dettagli>
                  </Riga>
                ) : (
                  <Riga key={i.id} a={`${base}/allenamenti/${i.id}`} data={i.dataOra}>
                    <span className="font-semibold">Allenamento</span>
                    <Dettagli>
                      <Dettaglio Icona={ClockIcon}>
                        {formatoOra.format(new Date(i.dataOra))}
                      </Dettaglio>
                      <Dettaglio Icona={MapPinIcon}>{i.luogo ?? 'Luogo da definire'}</Dettaglio>
                    </Dettagli>
                  </Riga>
                ),
              )}
            </CardSezione>

            <CardSezione titolo="Ultimi risultati" Icona={TrendingUpIcon}>
              {ultimiRisultati.length === 0 && <StatoVuoto titolo="Nessuna partita conclusa" />}
              {ultimiRisultati.map((p) => {
                const esito = esitoDi(p);
                return (
                  <Riga
                    key={p.id}
                    a={`${base}/partite/${p.id}/tabellino`}
                    data={p.dataOra}
                    fascia={COLORE_ESITO[esito]}
                    destra={
                      <span className="flex shrink-0 flex-col items-end gap-1">
                        <span className="text-xl font-bold whitespace-nowrap tabular-nums">
                          {punteggio(p, p.inCasa)}
                        </span>
                        <Badge
                          className={cn('px-1.5', COLORE_ESITO[esito])}
                          title={ETICHETTA_ESITO[esito]}
                        >
                          {LETTERA_ESITO[esito]}
                        </Badge>
                      </span>
                    }
                  >
                    <span className="truncate font-semibold">{p.avversario}</span>
                    <Dettagli>
                      <Dettaglio Icona={p.inCasa ? HouseIcon : BusIcon}>
                        {p.inCasa ? 'Casa' : 'Trasferta'}
                      </Dettaglio>
                      <Dettaglio Icona={TrophyIcon}>{p.competizione}</Dettaglio>
                    </Dettagli>
                    {p.marcatori.length > 0 && (
                      <span className="flex min-w-0 items-center gap-1.5 text-sm">
                        <IconaEvento tipo="gol" />
                        <span className="truncate">
                          {p.marcatori.map((m) => `${m.cognome} ${m.etichetta}`).join(', ')}
                        </span>
                      </span>
                    )}
                  </Riga>
                );
              })}
            </CardSezione>
          </div>

          <div className="flex flex-col gap-4 @4xl:gap-6">
            <CardSezione
              titolo="Avvisi"
              Icona={TriangleAlertIcon}
              conteggio={contaAvvisi(avvisi) > 0 ? contaAvvisi(avvisi) : undefined}
            >
              {contaAvvisi(avvisi) === 0 && (
                <p className="flex items-center justify-center gap-2 py-2 text-muted-foreground">
                  <CircleCheckIcon aria-hidden className="size-4 text-buono" />
                  Tutto in ordine.
                </p>
              )}
              {avvisi.statiSportivi.map((g) => (
                <Avviso key={`s-${g.id}-${g.competizione}`} a={`/giocatori/${g.id}`}>
                  <span className="font-semibold">
                    {g.nome} {g.cognome}
                  </span>
                  <CartellinoSportivo stato={g.stato} competizione={g.competizione} />
                </Avviso>
              ))}
              {avvisi.nonDisponibili.map((g) => (
                <Avviso key={`i-${g.id}`} a={`/giocatori/${g.id}`}>
                  <span className="font-semibold">
                    {g.nome} {g.cognome}
                  </span>
                  <BadgeStato stato={g.stato} />
                </Avviso>
              ))}
              {avvisi.daChiudere.map((p) => (
                <Avviso
                  key={`c-${p.id}`}
                  a={`${base}/partite/${p.id}`}
                  Icona={CalendarX2Icon}
                  quando={p.dataOra}
                >
                  <span>
                    <strong>{p.avversario}</strong>: data passata, va conclusa o annullata
                  </span>
                </Avviso>
              ))}
              {avvisi.senzaVoti?.map((p) => (
                <Avviso
                  key={`v-${p.id}`}
                  a={`${base}/partite/${p.id}/voti`}
                  Icona={StarIcon}
                  quando={p.dataOra}
                >
                  <span>
                    <strong>{p.avversario}</strong>: nessun voto
                  </span>
                </Avviso>
              ))}
              {avvisi.incoerenze.map((p) => (
                <Avviso
                  key={`e-${p.id}`}
                  a={`${base}/partite/${p.id}/modifica`}
                  Icona={ActivityIcon}
                  quando={p.dataOra}
                >
                  <span>
                    <strong>{p.avversario}</strong>:{' '}
                    {p.incoerenze === 1 ? '1 incoerenza' : `${p.incoerenze} incoerenze`} negli
                    eventi
                  </span>
                </Avviso>
              ))}
              {avvisi.senzaAppello.map((a) => (
                <Avviso
                  key={`a-${a.id}`}
                  a={`${base}/allenamenti/${a.id}/presenze`}
                  Icona={ClipboardXIcon}
                  quando={a.dataOra}
                >
                  <span>
                    <strong>Allenamento{a.luogo ? ` · ${a.luogo}` : ''}</strong>: svolto senza
                    appello
                  </span>
                </Avviso>
              ))}
            </CardSezione>

            <CardSezione titolo="Marcatori" Icona={GoalIcon}>
              {stagione.marcatori.length === 0 && <StatoVuoto titolo="Ancora nessun gol" />}
              {stagione.marcatori.length > 0 && (
                <ol className="flex flex-col overflow-hidden rounded-lg border border-border bg-card">
                  {stagione.marcatori.map((m, i) => (
                    <li key={m.id} className="border-b border-border last:border-b-0">
                      <Link to={`/giocatori/${m.id}`} className="flex items-center gap-3 px-3 py-2">
                        <span
                          className={cn(
                            'flex size-7 shrink-0 items-center justify-center rounded-full text-sm font-bold tabular-nums',
                            i === 0 ? 'bg-primary text-primary-foreground' : 'bg-muted',
                          )}
                        >
                          {i + 1}
                        </span>
                        <span className="min-w-0 flex-1 truncate">
                          {m.nome} <strong>{m.cognome}</strong>
                        </span>
                        <span className="flex items-center gap-1.5 font-bold tabular-nums">
                          <IconaEvento tipo="gol" />
                          {m.gol}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ol>
              )}
            </CardSezione>
          </div>
        </div>
      </Corpo>
    </div>
  );
}

const ETICHETTA_CRONOMETRO = {
  in_gioco: 'In gioco',
  tra_periodi: 'Intervallo',
  sospesa: 'Sospesa',
  terminata: 'Terminata',
} as const;

/** Nella banda in testa: la partita sul campo, con il risultato e il minuto. */
function SulCampo({
  base,
  partita: p,
}: {
  base: string;
  partita: NonNullable<DatiCruscotto['inCorso']>;
}) {
  const stato = p.cronometro
    ? p.cronometro.stato === 'in_gioco'
      ? p.cronometro.etichetta
      : ETICHETTA_CRONOMETRO[p.cronometro.stato]
    : 'Formazione pronta';
  return (
    <Link
      to={`${base}/partite/${p.id}`}
      className="flex items-center gap-4 rounded-lg bg-primary-foreground/10 p-4 hover:bg-primary-foreground/15 @3xl:min-w-80"
    >
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="flex items-center gap-2 text-sm font-semibold">
          {p.cronometro && (
            <span className="relative flex size-2.5">
              <span className="absolute inline-flex size-full animate-ping rounded-full bg-stato-infortunato opacity-75" />
              <span className="relative inline-flex size-2.5 rounded-full bg-stato-infortunato" />
            </span>
          )}
          {p.cronometro ? `In corso · ${stato}` : stato}
        </span>
        <span className="truncate text-lg font-semibold">{p.avversario}</span>
        <span className="truncate text-sm text-primary-foreground/75">{incontro(p)}</span>
      </span>
      <span className="text-3xl font-bold whitespace-nowrap tabular-nums">
        {punteggio(p, p.inCasa)}
      </span>
    </Link>
  );
}

const incontro = (p: PartitaInBreve) => `${p.inCasa ? 'Casa' : 'Trasferta'} · ${p.competizione}`;

/** Nella banda in testa: il prossimo impegno dei sette giorni, con quanto manca. */
function Prossimo({
  base,
  prossimo: p,
  adesso,
}: {
  base: string;
  prossimo: DatiCruscotto['prossimo'];
  adesso: Date;
}) {
  if (!p) {
    return (
      <p className="rounded-lg bg-primary-foreground/10 p-4 text-primary-foreground/75">
        Nessun impegno nei prossimi sette giorni.
      </p>
    );
  }
  const data = new Date(p.dataOra);
  const partita = p.tipo === 'partita';
  return (
    <Link
      to={`${base}/${partita ? 'partite' : 'allenamenti'}/${p.id}`}
      className="flex items-center gap-4 rounded-lg bg-primary-foreground/10 p-4 hover:bg-primary-foreground/15 @3xl:min-w-80"
    >
      <Foglietto data={p.dataOra} className="border-transparent" />
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-[13px] font-semibold tracking-wide uppercase text-primary-foreground/75">
          {quando(p.dataOra, adesso)} · {formatoOra.format(data)}
        </span>
        <span className="truncate text-lg font-semibold">
          {partita ? p.avversario : 'Allenamento'}
        </span>
        <span className="truncate text-sm text-primary-foreground/75">
          {partita
            ? `${incontro(p)} · ${p.convocati === 1 ? '1 convocato' : `${p.convocati} convocati`}`
            : `${p.luogo ?? 'Luogo da definire'} · ${p.rosa} in rosa`}
        </span>
      </span>
      <ChevronRightIcon aria-hidden className="size-5 shrink-0 text-primary-foreground/75" />
    </Link>
  );
}

/** Uno dei numeri della stagione: il valore grande, una nota e un dettaglio sotto. */
function Numero({
  a,
  Icona,
  etichetta,
  valore,
  nota,
  children,
}: {
  a: string;
  Icona: LucideIcon;
  etichetta: string;
  valore: ReactNode;
  nota: string;
  children?: ReactNode;
}) {
  return (
    <Link to={a} className="group rounded-xl">
      <Card className="h-full gap-3 transition-shadow group-hover:ring-foreground/30">
        <CardHeader>
          <CardDescription className="flex items-center gap-1.5 font-semibold tracking-wide uppercase">
            <Icona aria-hidden className="size-4" />
            {etichetta}
          </CardDescription>
          <CardTitle className="flex items-baseline gap-1.5 text-3xl font-bold tabular-nums">
            {valore}
          </CardTitle>
        </CardHeader>
        <CardContent className="mt-auto flex flex-col gap-2">
          {children}
          <span className="text-[13px] text-muted-foreground">{nota}</span>
        </CardContent>
      </Card>
    </Link>
  );
}

function Differenza({ valore }: { valore: number }) {
  return (
    <Badge
      variant="outline"
      className={cn(
        'tabular-nums',
        valore > 0 && 'border-buono text-buono',
        valore < 0 && 'border-destructive text-destructive',
      )}
    >
      {valore > 0 ? `+${valore}` : valore} differenza reti
    </Badge>
  );
}

/** Il foglietto con la data, lo stesso delle righe di partite e allenamenti. */
/**
 * Il foglietto della data, come nelle righe di partite e allenamenti: la
 * fascia nera del programmato o, per i risultati, il colore dell'esito.
 */
function Foglietto({
  data: iso,
  fascia = 'bg-primary text-primary-foreground',
  className,
}: {
  data: string;
  fascia?: string;
  className?: string;
}) {
  const data = new Date(iso);
  return (
    <span
      className={cn(
        'flex w-14 shrink-0 flex-col overflow-hidden rounded-md border border-border bg-background text-center text-foreground',
        className,
      )}
    >
      <span className={cn('py-0.5 text-[11px] font-semibold uppercase', fascia)}>
        {formatoGiornoSettimana.format(data).replace('.', '')}
      </span>
      <span className="text-xl leading-7 font-bold tabular-nums">
        {formatoNumeroGiorno.format(data)}
      </span>
      <span className="pb-0.5 text-[11px] text-muted-foreground uppercase">
        {formatoMeseBreve.format(data).replace('.', '')}
      </span>
    </span>
  );
}

function Riga({
  a,
  data,
  fascia,
  destra,
  children,
}: {
  a: string;
  data: string;
  fascia?: string;
  destra?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Link to={a} className="flex items-center gap-3 rounded-lg border border-border bg-card p-2.5">
      <Foglietto data={data} fascia={fascia} />
      <span className="flex min-w-0 flex-1 flex-col gap-1">{children}</span>
      {destra}
    </Link>
  );
}

function Dettagli({ children }: { children: ReactNode }) {
  return (
    <span className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-0.5 text-sm text-muted-foreground">
      {children}
    </span>
  );
}

function Dettaglio({ Icona, children }: { Icona: LucideIcon; children: ReactNode }) {
  return (
    <span className="flex min-w-0 items-center gap-1.5 tabular-nums">
      <Icona aria-hidden className="size-4 shrink-0" />
      <span className="truncate">{children}</span>
    </span>
  );
}

/**
 * Una riga degli avvisi: i giocatori con il loro badge, le partite e gli
 * allenamenti con l'icona del problema e la data.
 */
function Avviso({
  a,
  Icona,
  quando: iso,
  children,
}: {
  a: string;
  Icona?: LucideIcon;
  quando?: string;
  children: ReactNode;
}) {
  return (
    <Link
      to={a}
      className="flex items-center gap-3 rounded-lg border border-border bg-card px-3 py-2.5"
    >
      {Icona && (
        <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-avviso text-avviso-foreground">
          <Icona aria-hidden className="size-4" />
        </span>
      )}
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        {iso && (
          <span className="text-[13px] text-muted-foreground tabular-nums">
            {formatoRiga.format(new Date(iso))}
          </span>
        )}
        <span className="flex flex-wrap items-center gap-x-2 gap-y-1">{children}</span>
      </span>
    </Link>
  );
}

const PASSI: Record<
  ChiavePasso,
  { titolo: string; testo: string; azioni: { etichetta: string; a: string }[] }
> = {
  rosa: {
    titolo: 'Crea la rosa',
    testo:
      "Importa i giocatori da un'altra squadra-stagione, oppure tesserali uno a uno dalla rosa.",
    azioni: [
      { etichetta: 'Importa la rosa', a: 'rosa/import' },
      { etichetta: 'Vai alla rosa', a: 'rosa' },
    ],
  },
  impostazioni: {
    titolo: 'Configura formato di gioco e tipi di evento',
    testo:
      'Periodi, durata e giocatori in campo si copiano su ogni partita nuova; i tipi di evento sono i pulsanti del Live Timer.',
    azioni: [{ etichetta: 'Apri le impostazioni', a: 'impostazioni' }],
  },
  staff: {
    titolo: 'Assegna lo staff',
    testo: 'Mister e Dirigenti che lavoreranno su questa squadra.',
    azioni: [{ etichetta: 'Apri lo staff', a: 'staff' }],
  },
};

/** Squadra senza rosa, per l'Admin: i passi per prepararla, spuntati dai dati. */
function PassiGuidati({ base, passi }: { base: string; passi: Passi }) {
  return (
    <section>
      <p className="mb-4 text-muted-foreground">
        La squadra è appena stata creata: la Home si riempie quando c'è la rosa.
      </p>
      <ElencoPassi
        passi={elencoPassi(passi).map((p) => ({
          ...p,
          ...PASSI[p.chiave],
          azioni: PASSI[p.chiave].azioni.map((a) => ({ ...a, a: `${base}/${a.a}` })),
        }))}
      />
    </section>
  );
}

/** Squadra senza rosa, per Mister e Dirigente: la prepara l'Admin Squadra. */
function InPreparazione({ ss, programma }: { ss: string; programma: boolean }) {
  const navigate = useNavigate();
  const [nuovo, setNuovo] = useState(false);
  const [blocco, setBlocco] = useState(false);
  return (
    <section>
      <p className="mb-3 text-muted-foreground">
        La squadra è in preparazione presso l'Admin Squadra: la Home si riempie quando c'è la rosa.
        {programma && ' Intanto puoi già programmare gli allenamenti.'}
      </p>
      {programma && (
        <span className="flex flex-wrap gap-2">
          <Button onClick={() => setBlocco(true)}>Programma gli allenamenti</Button>
          <Button variant="outline" onClick={() => setNuovo(true)}>
            Nuovo allenamento
          </Button>
          <DialogAllenamento ss={ss} aperto={nuovo} onChiudi={() => setNuovo(false)} />
          <DialogBlocco
            ss={ss}
            aperto={blocco}
            onChiudi={() => setBlocco(false)}
            onCreati={() => navigate(`/sq/${ss}/allenamenti`)}
          />
        </span>
      )}
    </section>
  );
}
